type FindAddressComponentParams = {
  readonly addressComponents: readonly google.maps.GeocoderAddressComponent[];
  readonly type: string;
};

function findAddressComponent({
  addressComponents,
  type,
}: FindAddressComponentParams):
  | google.maps.GeocoderAddressComponent
  | undefined {
  return addressComponents.find((component) => component.types.includes(type));
}

export type LocationSelection = {
  readonly city: string;
  readonly formattedAddress: string;
  readonly lat: number;
  readonly lng: number;
  readonly location: string;
  readonly postalCode: string;
  readonly streetAddress: string;
};

type ParseAddressSelectionParams = {
  readonly latLng: google.maps.LatLngLiteral;
  readonly result: google.maps.GeocoderResult;
};

/** Parses a Google geocoder result into the location fields required by the form. */
export function parseAddressSelection({
  latLng,
  result,
}: ParseAddressSelectionParams): LocationSelection | null {
  if (!Number.isFinite(latLng.lat) || !Number.isFinite(latLng.lng)) {
    return null;
  }

  const addressComponents = result.address_components;
  const streetNumber = findAddressComponent({
    addressComponents,
    type: "street_number",
  });
  const street = findAddressComponent({ addressComponents, type: "route" });
  const postalTown =
    findAddressComponent({ addressComponents, type: "postal_town" }) ??
    findAddressComponent({ addressComponents, type: "locality" });
  const postalCode = findAddressComponent({
    addressComponents,
    type: "postal_code",
  });

  if (!street?.long_name || !postalTown?.long_name || !postalCode?.long_name) {
    return null;
  }

  return {
    city: postalTown.long_name,
    formattedAddress: result.formatted_address,
    lat: latLng.lat,
    lng: latLng.lng,
    location: `${latLng.lat},${latLng.lng}`,
    postalCode: postalCode.long_name.replace(/\s+/g, ""),
    streetAddress:
      `${street.long_name} ${streetNumber?.long_name ?? ""}`.trim(),
  };
}
